import { Truck, Briefcase, TrendingUp, MapPinned, Cpu, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaSection from "@/components/home/CtaSection";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/lib/content";

const ICONS = { Truck, Briefcase, TrendingUp, MapPinned, Cpu };

export default function Industries() {
  const { data } = useSite();
  const industries = data?.industries || [];
  const seo = data?.settings?.seo?.["/industries"];

  return (
    <>
      <Seo
        title={seo?.title || "Industries | Business Software Solutions — LogiTech Nexus"}
        siteName="LogiTech Nexus"
        description={seo?.description || "LogiTech Nexus builds software for transportation & logistics, professional services, field operations and growing businesses."}
      />
      <PageHero
        eyebrow="Industries"
        title="Software That Speaks"
        accent="Your Industry's Language."
        subtitle="Our software capabilities adapt to different industries and operating models. Here's where our approach fits best."
        testid="industries-hero"
      />

      <section className="bg-void pb-28" data-testid="industries-list">
        <div className="mx-auto max-w-7xl space-y-6 px-6 lg:px-10">
          {industries.map((ind, i) => {
            const Icon = ICONS[ind.icon] || Briefcase;
            return (
              <Reveal key={ind.id || ind.slug} delay={Math.min(i * 0.06, 0.3)} y={24}>
                <div
                  className="grid gap-8 rounded-2xl border border-slate-800/80 bg-navy/60 p-8 transition-[border-color] duration-500 hover:border-blue-500/40 sm:p-10 lg:grid-cols-[auto_1fr_1fr] lg:items-center"
                  data-testid={`industry-row-${ind.slug}`}
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                    <Icon size={28} />
                  </span>
                  <div>
                    <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">{ind.name}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{ind.summary}</p>
                  </div>
                  {ind.points?.length > 0 && (
                    <ul className="space-y-2.5">
                      {ind.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                          <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-blue-400" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mx-auto mt-14 max-w-7xl px-6 lg:px-10">
          <p className="rounded-xl border border-slate-800/70 bg-navy/40 px-6 py-4 text-xs leading-relaxed text-slate-500" data-testid="industries-disclaimer">
            Don't see your industry? Our process starts with understanding how your
            business operates — the software follows from there. Reach out and tell
            us about your operation.
          </p>
        </Reveal>
      </section>

      <CtaSection />
    </>
  );
}
